import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { Mail, Phone, Clock, MapPin, ArrowRight, User } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { SocialRow } from "../components/Socials";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass =
  "w-full bg-ct3f-elevated border border-white/15 px-4 py-3 text-white placeholder-white/35 focus:outline-none focus:border-ct3f-orange focus:ring-0 transition-colors";

export default function Contact() {
  const { t, lang } = useI18n();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    service: searchParams.get("service") || "",
    message: "",
    vehicleBrand: "",
    vehicleModel: "",
    vin: "",
  });

  const cocService = t.services.list.find((s) => s.k === "S/05");
  const isCoC = !!cocService && form.service === cocService.t;
  const [consent, setConsent] = useState(false);
  const termsSlug = { fr: "cgv", de: "agb", en: "terms" }[lang] || "cgv";

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim(),
        service: form.service || null,
        message: form.message.trim(),
        vehicle_brand: form.vehicleBrand.trim() || null,
        vehicle_model: form.vehicleModel.trim() || null,
        vin: form.vin.trim() || null,
        locale: lang,
      };
      await axios.post(`${API}/contact`, payload);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", phone: "", subject: "", service: "", message: "", vehicleBrand: "", vehicleModel: "", vin: "" });
      setConsent(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || t.contact.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-ct3f-bg text-white">
      <section className="border-b border-white/10 bg-flame-radial">
        <div className="px-6 md:px-12 lg:px-20 py-20 lg:py-28 relative">
          <div className="absolute inset-0 grid-lines pointer-events-none opacity-50" />
          <div className="relative">
            <span className="label-tech text-ct3f-amber">{t.contact.eyebrow}</span>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.92] max-w-5xl">
              <span className="text-white">{t.contact.title1}{" "}</span>
              <span className="text-flame">{t.contact.title2}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-white/70 text-lg">{t.contact.sub}</p>
          </div>
        </div>
      </section>

      <section className="bg-ct3f-bg">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          {/* FORM */}
          <div className="lg:col-span-8 px-6 md:px-12 lg:px-20 py-16 lg:py-20 border-b lg:border-b-0 lg:border-r border-white/10">
            <span className="label-tech text-white/40">FORMULAIRE · FORMULAR · FORM</span>
            <form onSubmit={onSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
              <div>
                <label className="label-tech text-white/55 mb-2 block">
                  {t.contact.form.name} <span className="text-ct3f-amber">*</span>
                </label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder={t.contact.form.namePh}
                  className={inputClass}
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="label-tech text-white/55 mb-2 block">
                  {t.contact.form.email} <span className="text-ct3f-amber">*</span>
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder={t.contact.form.emailPh}
                  className={inputClass}
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <label className="label-tech text-white/55 mb-2 block">{t.contact.form.phone}</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder={t.contact.form.phonePh}
                  className={inputClass}
                  data-testid="contact-input-phone"
                />
              </div>
              <div>
                <label className="label-tech text-white/55 mb-2 block">{t.contact.form.service}</label>
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className={inputClass}
                  data-testid="contact-input-service"
                >
                  <option value="" className="bg-ct3f-elevated">{t.contact.form.servicePh}</option>
                  {t.services.list.map((s) => (
                    <option key={s.k} value={s.t} className="bg-ct3f-elevated">
                      {s.k} — {s.t}
                    </option>
                  ))}
                </select>
              </div>
              {isCoC && (
                <div
                  className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-5 border border-ct3f-orange/40 bg-ct3f-elevated/60 p-5"
                  data-testid="contact-coc-fields"
                >
                  <div className="md:col-span-3">
                    <span className="label-tech text-ct3f-amber">{t.contact.form.cocBlock}</span>
                  </div>
                  <div>
                    <label className="label-tech text-white/55 mb-2 block">
                      {t.contact.form.brand} <span className="text-ct3f-amber">*</span>
                    </label>
                    <input
                      required
                      name="vehicleBrand"
                      value={form.vehicleBrand}
                      onChange={onChange}
                      placeholder={t.contact.form.brandPh}
                      className={inputClass}
                      data-testid="contact-input-brand"
                    />
                  </div>
                  <div>
                    <label className="label-tech text-white/55 mb-2 block">
                      {t.contact.form.model} <span className="text-ct3f-amber">*</span>
                    </label>
                    <input
                      required
                      name="vehicleModel"
                      value={form.vehicleModel}
                      onChange={onChange}
                      placeholder={t.contact.form.modelPh}
                      className={inputClass}
                      data-testid="contact-input-model"
                    />
                  </div>
                  <div>
                    <label className="label-tech text-white/55 mb-2 block">{t.contact.form.vin}</label>
                    <input
                      name="vin"
                      value={form.vin}
                      onChange={onChange}
                      placeholder={t.contact.form.vinPh}
                      className={inputClass}
                      data-testid="contact-input-vin"
                    />
                  </div>
                </div>
              )}
              <div className="md:col-span-2">
                <label className="label-tech text-white/55 mb-2 block">
                  {t.contact.form.subject} <span className="text-ct3f-amber">*</span>
                </label>
                <input
                  required
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  placeholder={t.contact.form.subjectPh}
                  className={inputClass}
                  data-testid="contact-input-subject"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-tech text-white/55 mb-2 block">
                  {t.contact.form.message} <span className="text-ct3f-amber">*</span>
                </label>
                <textarea
                  required
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder={t.contact.form.messagePh}
                  rows={6}
                  className={`${inputClass} resize-none`}
                  data-testid="contact-input-message"
                />
              </div>
              <div className="md:col-span-2 pt-2">
                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="contact-consent-label">
                  <input
                    required
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 appearance-none border border-white/30 bg-ct3f-elevated checked:bg-ct3f-orange checked:border-ct3f-orange transition-colors cursor-pointer"
                    data-testid="contact-consent-checkbox"
                  />
                  <span className="text-sm text-white/70">
                    {t.contact.form.consentPre}{" "}
                    <Link
                      to={`/${termsSlug}`}
                      target="_blank"
                      className="text-ct3f-amber underline underline-offset-2 hover:text-ct3f-orange transition-colors"
                      data-testid="contact-consent-terms-link"
                    >
                      {t.contact.form.consentLink}
                    </Link>{" "}
                    <span className="text-ct3f-amber">*</span>
                  </span>
                </label>
              </div>
              <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
                <p className="label-tech text-white/40 hidden md:block">CT3F · LU + FR + BE</p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-3 bg-flame text-white label-tech px-8 py-4 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed ring-flame transition-all"
                >
                  {submitting ? t.common.sending : t.common.submit}
                  <ArrowRight size={16} className="hover-arrow" />
                </button>
              </div>
            </form>
          </div>

          {/* INFO PANEL */}
          <aside className="lg:col-span-4 bg-[#0E0E10] text-white relative" data-testid="contact-info-panel">
            <div
              className="absolute inset-0 opacity-50 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 90% 0%, rgba(242,90,41,0.18), transparent 55%), radial-gradient(circle at 0% 100%, rgba(248,154,43,0.10), transparent 55%)",
              }}
            />
            <div className="relative px-6 md:px-12 lg:px-10 py-16 lg:py-20 h-full flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <LogoMark size={32} />
                <span className="label-tech text-ct3f-amber">{t.contact.info.title}</span>
              </div>

              <div className="mt-8 space-y-8">
                <div>
                  <div className="label-tech text-white/40">{t.contact.info.contactLabel}</div>
                  <div
                    className="mt-2 flex items-center gap-3 font-display text-xl lg:text-2xl font-bold tracking-tight"
                    data-testid="contact-info-person"
                  >
                    <User size={20} strokeWidth={1.5} className="text-ct3f-amber" />
                    {t.contact.info.contactValue}
                  </div>
                </div>

                <div>
                  <div className="label-tech text-white/40">{t.contact.info.emailLabel}</div>
                  <a
                    href="mailto:ctluxembourg@outlook.com"
                    className="mt-2 flex items-center gap-3 font-display text-xl lg:text-[1.55rem] font-bold tracking-tight hover:text-ct3f-amber transition-colors break-all"
                    data-testid="contact-info-email"
                  >
                    <Mail size={20} strokeWidth={1.5} className="text-ct3f-amber shrink-0" />
                    ctluxembourg@outlook.com
                  </a>
                  <a
                    href="mailto:info@ct3f.com"
                    className="mt-3 flex items-center gap-3 font-display text-lg lg:text-xl font-semibold tracking-tight text-white/85 hover:text-ct3f-amber transition-colors break-all"
                    data-testid="contact-info-email-alt"
                  >
                    <Mail size={18} strokeWidth={1.5} className="text-ct3f-amber/70 shrink-0" />
                    info@ct3f.com
                  </a>
                </div>

                <div>
                  <div className="label-tech text-white/40">{t.contact.info.phoneLabelLu}</div>
                  <a
                    href="tel:+352621739411"
                    className="mt-2 flex items-center gap-3 font-display text-xl lg:text-2xl font-bold tracking-tight hover:text-ct3f-amber transition-colors"
                    data-testid="contact-info-phone-lu"
                  >
                    <Phone size={20} strokeWidth={1.5} className="text-ct3f-amber" />
                    +352 621 739 411
                  </a>
                </div>

                <div>
                  <div className="label-tech text-white/40">{t.contact.info.phoneLabelBe}</div>
                  <a
                    href="tel:+32470456536"
                    className="mt-2 flex items-center gap-3 font-display text-xl lg:text-2xl font-bold tracking-tight hover:text-ct3f-amber transition-colors"
                    data-testid="contact-info-phone-be"
                  >
                    <Phone size={20} strokeWidth={1.5} className="text-ct3f-amber" />
                    +32 470 45 65 36
                  </a>
                </div>

                <div>
                  <div className="label-tech text-white/40">{t.contact.info.hoursLabel}</div>
                  <div className="mt-2 flex items-center gap-3 font-display text-lg font-semibold">
                    <Clock size={18} strokeWidth={1.5} className="text-ct3f-amber" />
                    {t.contact.info.hoursValue}
                  </div>
                </div>

                <div>
                  <div className="label-tech text-white/40">{t.contact.info.regionsLabel}</div>
                  <div className="mt-2 flex items-center gap-3 font-display text-lg font-semibold">
                    <MapPin size={18} strokeWidth={1.5} className="text-ct3f-amber" />
                    {t.contact.info.regionsValue}
                  </div>
                  <div className="mt-4 flex gap-2">
                    {["LU", "FR", "BE"].map((c) => (
                      <span
                        key={c}
                        className="label-tech border border-ct3f-orange/50 text-ct3f-amber px-2 py-1"
                      >
                        [ {c} ]
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="label-tech text-white/40">SUIVEZ-NOUS · FOLGEN · FOLLOW</div>
                  <div className="mt-3">
                    <SocialRow variant="amber" testidPrefix="contact-social" />
                  </div>
                </div>
              </div>

              <div className="mt-auto pt-12 border-t border-white/10">
                <span className="label-tech text-white/35">CT3F · 2025</span>
                <p className="mt-2 text-white/45 text-sm">
                  Contrôle Technique des trois Frontières
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
